/**
 * Нормализует текст и ограничивает «залго»/комбинирующие символы.
 * - NFC/NFKC нормализация (совместимость)
 * - Удаляет управляющие (Cc), большинство невидимых форматирующих (Cf), нулевую ширину
 * - Ограничивает подряд идущие комбинирующие знаки \p{M} до maxCombiningInRow
 */
// sanitizeUnicodeText: нормализует и очищает строку от опасных/невидимых символов,
// ограничивает «залго» (комбинирующие знаки), приводит переводы строк, пробелы и т.п.
export function sanitizeUnicodeText(
  input: string,
  options?: {
    normalization?: "NFC" | "NFD" | "NFKC" | "NFKD";
    maxCombiningInRow?: number;
    removeControlChars?: boolean;
    removeZeroWidth?: boolean;
    stripBidi?: boolean; // удалить BiDi управляющие символы
    stripPrivateUse?: boolean; // удалить Private Use Area символы
    stripUnpairedSurrogates?: boolean; // удалить одиночные суррогаты
    normalizeNewlines?: boolean; // CRLF/CR -> LF
    collapseWhitespace?: boolean; // коллапсировать множественные пробелы/табы
  },
): string {
  const normalization = options?.normalization ?? "NFKC";
  const maxCombiningInRow = Math.max(0, options?.maxCombiningInRow ?? 2);
  const removeControlChars = options?.removeControlChars ?? true;
  const removeZeroWidth = options?.removeZeroWidth ?? true;
  const stripBidi = options?.stripBidi ?? true;
  const stripPrivateUse = options?.stripPrivateUse ?? true;
  const stripUnpairedSurrogates = options?.stripUnpairedSurrogates ?? true;
  const normalizeNewlines = options?.normalizeNewlines ?? true;
  const collapseWhitespace = options?.collapseWhitespace ?? true;

  let text = input.normalize(normalization);

  if (normalizeNewlines) {
    // CRLF/CR -> LF
    text = text.replace(/\r\n?/g, "\n");
  }

  if (removeControlChars) {
    // Удаляем Cc (управляющие), кроме таб/новая строка (после нормализации CR уже убран)
    text = text.replace(/[\p{Cc}]/gu, (ch) =>
      ch === "\t" || ch === "\n" ? ch : "",
    );
  }

  if (removeZeroWidth || stripBidi) {
    // Удаляем Cf нулевой ширины и BiDi overrides/isolates
    // ZWSP U+200B, ZWNJ U+200C, ZWJ U+200D, SHY U+00AD, WJ U+2060,
    // LRM/RLM U+200E/U+200F, BiDi U+202A–U+202E, U+2066–U+2069, U+2060–U+2064
    text = text.replace(
      /[\u00AD\u200B-\u200F\u202A-\u202E\u2060-\u2064\u2066-\u2069]/g,
      "",
    );
  }

  if (stripPrivateUse) {
    // BMP Private Use U+E000–U+F8FF
    text = text.replace(/[\uE000-\uF8FF]/g, "");
    // SMP/Plane 15-16 Private Use: U+F0000–U+FFFFD, U+100000–U+10FFFD (суррогатные пары)
    text = text.replace(/[\uDB80-\uDBBF][\uDC00-\uDFFF]/g, "");
  }

  if (stripUnpairedSurrogates) {
    // Удаляем одиночные суррогаты
    text = text.replace(
      /[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g,
      "",
    );
  }

  // Ограничиваем количество подряд идущих комбинирующих символов (\p{M})
  if (maxCombiningInRow >= 0) {
    text = text.replace(/\p{M}+/gu, (seq) => seq.slice(0, maxCombiningInRow));
  }

  if (collapseWhitespace) {
    // Коллапс пробелов/табов, не трогая перевод строк
    text = text.replace(/[ \t]{2,}/g, " ");
  }

  return text;
}

// sanitizeForUi: преднастроенный санитайзер для UI полей ввода
// (включает все защитные опции, кроме ограничения повторов пунктуации)
export function sanitizeForUi(input: string): string {
  return sanitizeUnicodeText(input, {
    normalization: "NFKC",
    maxCombiningInRow: 2,
    removeControlChars: true,
    removeZeroWidth: true,
    stripBidi: true,
    stripPrivateUse: true,
    stripUnpairedSurrogates: true,
    normalizeNewlines: true,
    collapseWhitespace: true,
  });
}
